/** @format */
import {ReactNode} from 'react';

import {MonitorOutlined, SensorsOutlined, TornadoOutlined} from '@mui/icons-material';

import {useNavigate} from 'react-router-dom';

import {Column, Root} from './styles';
import mediaplanBg from '../../images/anim_mediaplan.svg';
import funnelBg from '../../images/anim_finnel.svg';
import feedsBg from '../../images/anim_feeds.svg';
import sensorsBg from '../../images/anim_sensors.svg';
import {HotspotSmall} from '../../components/HotspotSmall';
import {HotspotKey, HotspotStatusList} from '../../../../types';
import {HotspotSmallFullness} from '../../types';
import {spreadSmallBlocks} from '../../helpers/spreadSmallBlocks';
import {getHotspotVariant} from '../../helpers/getHotspotVariant';
import {RenderHelpTooltip} from './RenderHelpTooltip';
import {HOTSPOT_HEADERS} from '../constants';

interface Props {
  statusList: HotspotStatusList;
  filteredKeys: HotspotKey[];
  onExpand: (key: HotspotKey) => void;
  onSetup: (key: HotspotKey) => void;
  companyId: string;
  daysLeft: number;
  isTrial: boolean;
}

const HotspotLayoutSmall = ({
  statusList,
  filteredKeys,
  daysLeft,
  isTrial,
  onExpand,
  companyId,
  onSetup,
}: Props) => {
  const navigate = useNavigate();

  if (!filteredKeys.length) {
    return null;
  }

  const {column1, column2} = spreadSmallBlocks(filteredKeys);

  const renderColumn = (columnKeys: HotspotKey[]): ReactNode => {
    if (!columnKeys.length) {
      return null;
    }

    return (
      <Column>
        {columnKeys.map((key, _, arr) => {
          const {isReady} = statusList[key];

          const fullness: HotspotSmallFullness = arr.length === 1 ? 'full' : 'compact';

          const variant = getHotspotVariant({
            daysLeft,
            isTrial,
            isHighlight: false,
            hotspotKey: key,
          });

          switch (key) {
            case 'mediaplan':
              return (
                <HotspotSmall
                  key={key}
                  variant={variant}
                  bgImage={mediaplanBg}
                  onExpand={() => onExpand('mediaplan')}
                  onSetup={() => {
                    onSetup('mediaplan');
                    navigate(`/home/project/periods?companyId=${companyId}&edit=1`);
                  }}
                  isReady={isReady}
                  fullness={fullness}
                  content={{
                    headerText: HOTSPOT_HEADERS.mediaplan,
                    subHeaderText: 'Первым делом',
                    HeaderIcon: <MonitorOutlined />,
                    HelpComponent: <RenderHelpTooltip hotspotKey='mediaplan' />,
                    buttonText: 'Настроить',
                  }}
                  bgOpacity={1}
                />
              );
            case 'funnel':
              return (
                <HotspotSmall
                  key={key}
                  variant={variant}
                  bgImage={funnelBg}
                  onExpand={() => onExpand('funnel')}
                  onSetup={() => {
                    onSetup('funnel');
                    navigate(`/home/project/funnel?companyId=${companyId}&edit=1`);
                  }}
                  isReady={isReady}
                  fullness={fullness}
                  content={{
                    headerText: HOTSPOT_HEADERS.funnel,
                    HeaderIcon: <TornadoOutlined />,
                    HelpComponent: <RenderHelpTooltip hotspotKey='funnel' />,
                    buttonText: 'Настроить',
                  }}
                  bgOpacity={0.4}
                />
              );
            case 'sensors':
              return (
                <HotspotSmall
                  key={key}
                  variant={variant}
                  bgImage={sensorsBg}
                  onExpand={() => onExpand('sensors')}
                  onSetup={() => {
                    onSetup('sensors');
                    navigate(`/home/project/sensors?companyId=${companyId}&edit=1`);
                  }}
                  isReady={isReady}
                  fullness={fullness}
                  content={{
                    headerText: HOTSPOT_HEADERS.sensors,
                    HeaderIcon: <SensorsOutlined />,
                    HelpComponent: <RenderHelpTooltip hotspotKey='sensors' />,
                    buttonText: 'Выбрать нужные',
                  }}
                  bgOpacity={1}
                />
              );
            case 'feeds':
              return (
                <HotspotSmall
                  key={key}
                  variant={variant}
                  bgImage={feedsBg}
                  onExpand={() => onExpand('feeds')}
                  onSetup={() => {
                    onSetup('feeds');
                    navigate(`/home/project/feeds?companyId=${companyId}&edit=1`);
                  }}
                  isReady={isReady}
                  fullness={fullness}
                  content={{
                    headerText: HOTSPOT_HEADERS.feeds,
                    HelpComponent: <RenderHelpTooltip hotspotKey='feeds' />,
                    buttonText: 'Настроить',
                  }}
                  bgOpacity={0.4}
                />
              );
            default:
              return null;
          }
        })}
      </Column>
    );
  };

  return (
    <Root>
      {renderColumn(column1)}
      {renderColumn(column2)}
    </Root>
  );
};

export {HotspotLayoutSmall};
